import { Box, Container, Grid2 } from "@mui/material"
import patterns from "../../../assets/imgs/pattens/Path.png"

const featureSelectionItems = [
  {
    title: "Sharper Selection",
    detail:
      "Score new submissions and renewals against real-world loss patterns by segment, peril, and venue.",
  },
  {
    title: "Pricing Signals",
    detail:
      "Surface severity and frequency drivers to inform rate, limits, deductibles, and attachment points.",
  },
  {
    title: "Product Design",
    detail:
      "Identify coverage gaps and feature opportunities from claim outcome trends and exposures.",
  },
  {
    title: "Portfolio Foresight",
    detail:
      "Spot emerging risks and regional shifts early to rebalance appetite and growth strategy.",
  },
  {
    title: "Risk Benchmarks",
    detail:
      "Leverage segmented risk benchmarks and compare with operations data to understand, control, and prevent losses with clear mitigation strategies.",
  },
]

const howItWorksItems = [
  {
    title: "1) Claim Grounded Signals",
    detail:
      "Domain-tuned NLP and pattern mining across 60M+ claims extract causal drivers of loss: injury and damage types, treatment pathways, venue effects, party roles, entity behavior, and time-to-event milestones.",
  },
  {
    title: "2) Risk and Product Scoring",
    detail:
      "Submission-level and segment-level scores for severity, litigation likelihood, and loss amplification signals, each with explainable factors and confidence bands.",
  },
  {
    title: "3) Action in Workflow",
    detail:
      "Scores and explanations are delivered via APIs, underwriting workbench widgets, dashboards, and Charlee AI Agents for appetite checks and what-if exploration.",
  },
]

const useCaseItems = [
  {
    title: "Operational Risk Detection",
    detail:
      "Integrate real-time intelligence with core and operational systems to detect exposures and alert operators before liability events escalate.",
  },
  {
    title: "Incident Severity Prediction",
    detail:
      "Classify on-site incidents by predicted medical cost, litigation risk, and reputational exposure to prioritize response and documentation.",
  },
  {
    title: "Workforce Safety Insights",
    detail:
      "Correlate payroll, shift patterns, and training records with injury trends to identify high-risk roles, time windows, and procedural gaps.",
  },
  {
    title: "Policy and Compliance Optimization",
    detail:
      "Cross-reference SOPs and policy terms to flag non-compliance risks that can increase exposure or void coverage.",
  },
  {
    title: "Premium and Claims Optimization",
    detail:
      "Feed aggregated operational and behavioral data into underwriting models to enable dynamic premiums and reward proactive prevention.",
  },
  {
    title: "Benchmarking and Peer Comparisons",
    detail:
      "Benchmark venue risk signals against anonymized peers across restaurants, bars, cafes, and hotels for stronger negotiations and prevention.",
  },
  {
    title: "Portfolio Intelligence",
    detail:
      "Use aggregated signals across operators to identify emerging trends and adjust appetite and pricing across hospitality classes.",
  },
]

const businessValues = [
  "Reduce loss ratio volatility with earlier detection of severity and litigation drivers.",
  "Improve hit ratio on in-appetite risks while curbing adverse selection.",
  "Accelerate time to underwrite through triage and explainable scoring.",
  "Inform rate filings and claim-product updates with grounded evidence.",
]

const metrics = [
  { value: "15-30%", label: "Fewer high-severity surprises in target segments" },
  { value: "5-10 pts", label: "Improvement in risk-adjusted hit ratio" },
  { value: "40% faster", label: "Dashboards ready with rapid time-to-value" },
  { value: "Weeks -> Days", label: "Signal delivery for new initiatives" },
]

const trustItems = [
  "Data residency and access controls aligned to carrier policies.",
  "PII handling, encryption in transit/at rest, and full audit trails.",
  "Explainable factors on every score with bias and drift monitoring across segments.",
  "Governance aligned to Charlee ethical AI principles.",
]

export const RiteCharleeSections = () => {
  return (
    <>
      <Box className="riteFeature-section w-full relative pt-6">
        <div
          className="absolute w-full top-0 left-0 h-[420px] z-0"
          style={{ backgroundImage: `url(${patterns})`, backgroundRepeat: "repeat no-repeat" }}
        />
        <Container maxWidth="lg">
          <Box className="relative z-10">
            <h4 className="rite-sectionTitle text-[29px] md:text-[42px] leading-[40px] md:leading-[54.6px] font-medium py-8">
              Feature Selection
            </h4>
            <Grid2 container spacing={3}>
              {featureSelectionItems.map((item, key) => (
                <Grid2 key={key} size={{ xs: 12, md: key === 4 ? 12 : 6, lg: key === 4 ? 12 : 6 }} className="rite-card rounded-xl bg-white shadow-xl p-8">
                  <h5 className="text-[25px] leading-[34px] font-medium pb-3">{item.title}</h5>
                  <p>{item.detail}</p>
                </Grid2>
              ))}
            </Grid2>
          </Box>
        </Container>
      </Box>

      <Box className="riteWorks-section w-full py-20 bg-[#021744] text-white">
        <Container maxWidth="lg">
          <h4 className="rite-sectionTitle text-[29px] md:text-[42px] leading-[40px] md:leading-[54.6px] font-medium">
            How RiteCharlee Works
          </h4>
          <Grid2 container spacing={3} className="pt-10">
            {howItWorksItems.map((item, key) => (
              <Grid2 key={key} size={{ xs: 12, md: 4 }} className="rite-card border border-white/20 rounded-xl p-6">
                <h5 className="text-[22px] leading-[30px] font-medium pb-4">{item.title}</h5>
                <p className="text-white/90">{item.detail}</p>
              </Grid2>
            ))}
          </Grid2>
          <p className="rite-card pt-8 text-white/90">
            <span className="font-medium">Integrations: </span>
            Native APIs, underwriting workbenches, data lakes, policy admin/rating, and
            claims systems (Guidewire, Duck Creek, Majesco, and more).
          </p>
        </Container>
      </Box>

      <Box className="riteUseCases-section w-full py-20">
        <Container maxWidth="lg">
          <h4 className="rite-sectionTitle text-[29px] md:text-[42px] leading-[40px] md:leading-[54.6px] font-medium pb-10">
            High-Impact Use Cases
          </h4>
          <Grid2 container spacing={3}>
            {useCaseItems.map((item, key) => (
              <Grid2 key={key} size={{ xs: 12, md: 6 }} className="rite-card rounded-xl bg-white shadow-lg p-7">
                <h5 className="text-[23px] leading-[31px] font-medium pb-3">{item.title}</h5>
                <p>{item.detail}</p>
              </Grid2>
            ))}
          </Grid2>
        </Container>
      </Box>

      <Box className="riteBusiness-section w-full py-20 bg-[#021744] text-white">
        <Container maxWidth="lg">
          <h4 className="rite-sectionTitle text-[29px] md:text-[42px] leading-[40px] md:leading-[54.6px] font-medium">
            Business Value
          </h4>
          <Box className="rite-card pt-8 text-left">
            <ul className="list-disc list-outside pl-6 space-y-2 text-left">
              {businessValues.map((item, key) => (
                <li key={key}>{item}</li>
              ))}
            </ul>
          </Box>
          <Grid2 container spacing={3} className="pt-10">
            {metrics.map((metric, key) => (
              <Grid2 key={key} size={{ xs: 12, sm: 6, md: 3 }}>
                <Box className="rite-card border border-white/20 rounded-xl p-6 h-full">
                  <h5 className="text-[33px] leading-[40px] font-medium text-[#00B7CC]">{metric.value}</h5>
                  <p className="pt-3 text-white/90">{metric.label}</p>
                </Box>
              </Grid2>
            ))}
          </Grid2>
        </Container>
      </Box>

      <Box className="riteSecurity-section w-full py-20">
        <Container maxWidth="lg">
          <h4 className="rite-sectionTitle text-[29px] md:text-[42px] leading-[40px] md:leading-[54.6px] font-medium pb-8">
            Security, Privacy and Ethical AI
          </h4>
          <Box className="rite-card rounded-xl bg-white shadow-lg p-8 text-left">
            <ul className="list-disc list-outside pl-6 space-y-2 text-left">
              {trustItems.map((item, key) => (
                <li key={key}>{item}</li>
              ))}
            </ul>
          </Box>
        </Container>
      </Box>
    </>
  )
}
